type DraftSaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

type DirtyRack = { isDirty?: boolean }
type RackSnapshot<T extends DirtyRack> = Record<string, T>

type DraftRevisionAcknowledgement<T extends DirtyRack> = {
  sentRevision: number
  acceptedRevision: number
  latestStartedRevision: number
  sentRacks: RackSnapshot<T>
  currentRacks: RackSnapshot<T>
  currentSaveStatus: DraftSaveStatus
}

type EditorSavePatch<T extends DirtyRack> = {
  racks?: RackSnapshot<T>
  baselineRacks: RackSnapshot<T>
  saveStatus: 'dirty' | 'saved'
}

type DraftSaveFlow<T extends DirtyRack, TResult> = {
  sentRevision: number
  getLatestStartedRevision: () => number
  sentRacks: RackSnapshot<T>
  request: () => Promise<TResult>
  getCurrentState: () => {
    racks: RackSnapshot<T>
    saveStatus: DraftSaveStatus
  }
  applyState: (patch: EditorSavePatch<T>) => void
  reportFailure: () => void
  onRejected?: (error: unknown) => void
}

function cleanRackSnapshot<T extends DirtyRack>(racks: RackSnapshot<T>): RackSnapshot<T> {
  return Object.fromEntries(
    Object.entries(racks).map(([id, rack]) => [id, { ...rack, isDirty: false }]),
  ) as RackSnapshot<T>
}

async function saveDraftRevision<T>(
  expectedRevision: number,
  request: () => Promise<T>,
): Promise<{ acceptedRevision: number; value: T }> {
  const value = await request()
  return { acceptedRevision: expectedRevision, value }
}

function isLatestDraftRevision(
  sentRevision: number,
  latestStartedRevision: number,
): boolean {
  return sentRevision === latestStartedRevision
}

function acknowledgeDraftRevision<T extends DirtyRack>({
  sentRevision,
  acceptedRevision,
  latestStartedRevision,
  sentRacks,
  currentRacks,
  currentSaveStatus,
}: DraftRevisionAcknowledgement<T>): EditorSavePatch<T> | null {
  if (acceptedRevision !== sentRevision || !isLatestDraftRevision(sentRevision, latestStartedRevision)) return null

  const baselineRacks = cleanRackSnapshot(sentRacks)
  if (currentSaveStatus === 'dirty') {
    return { baselineRacks, saveStatus: 'dirty' }
  }

  return {
    racks: cleanRackSnapshot(currentRacks),
    baselineRacks,
    saveStatus: 'saved',
  }
}

export async function executeDraftSaveFlow<T extends DirtyRack, TResult>({
  sentRevision,
  getLatestStartedRevision,
  sentRacks,
  request,
  getCurrentState,
  applyState,
  reportFailure,
  onRejected,
}: DraftSaveFlow<T, TResult>): Promise<TResult | null> {
  try {
    const { acceptedRevision, value } = await saveDraftRevision(sentRevision, request)
    const current = getCurrentState()
    const acknowledgement = acknowledgeDraftRevision({
      sentRevision,
      acceptedRevision,
      latestStartedRevision: getLatestStartedRevision(),
      sentRacks,
      currentRacks: current.racks,
      currentSaveStatus: current.saveStatus,
    })
    if (!acknowledgement) return null
    applyState(acknowledgement)
    return acknowledgement.saveStatus === 'saved' ? value : null
  } catch (error) {
    onRejected?.(error)
    if (isLatestDraftRevision(sentRevision, getLatestStartedRevision())) {
      reportFailure()
    }
    return null
  }
}

export async function executeDraftPublishFlow<T>({
  save,
  confirm,
  publish,
}: {
  save: () => Promise<T | null | undefined>
  confirm: () => Promise<boolean>
  publish: (saved: T) => Promise<unknown>
}): Promise<boolean> {
  const saved = await save()
  if (saved == null) return false
  if (!await confirm()) return false
  await publish(saved)
  return true
}
